/**
 * Asset Register — cross-client / cross-site paginated view over all imported
 * assets (~12k rows). Multi-select filters for Client → Site (dynamically
 * scoped by picked clients) → Asset Type + free-text search + condition band.
 *
 * Implements the immediate ask: "filter to include all clients, by client then
 * site, asset type" from the Aug 2026 scope brief (§9 Asset Intelligence,
 * §4.1 Standard Filters).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, X, ChevronDown, ChevronRight, Building2, MapPin, Layers, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt$ = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v || 0);
const fmtN = (v) => new Intl.NumberFormat("en-AU").format(v || 0);


export default function AssetRegister() {
  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  // ---- Persistent filter state (survives tab switching within Capital Planning) ----
  const LS_KEY = "gbe_asset_register_filters_v1";
  const initial = (() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
  })();
  const [page, setPage] = useState(initial.page || 1);
  const [pageSize] = useState(100);
  const [sort, setSort] = useState(initial.sort || "cost:desc");
  const [pickedClients, setPickedClients] = useState(initial.clients || []);
  const [pickedSites, setPickedSites] = useState(initial.sites || []);
  const [pickedTypes, setPickedTypes] = useState(initial.types || []);
  const [search, setSearch] = useState(initial.search || "");
  const [condMin, setCondMin] = useState(initial.condMin ?? 0);
  const [condMax, setCondMax] = useState(initial.condMax ?? 5);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({ clients: true, sites: false, types: false });

  // In-filter search boxes (own the visible-list scoping)
  const [clientQ, setClientQ] = useState("");
  const [siteQ, setSiteQ] = useState("");
  const [typeQ, setTypeQ] = useState("");

  // Persist filter state to localStorage on every change (broadcasts to
  // CapitalContext + Portfolio View via same-window custom event).
  useEffect(() => {
    try {
      const payload = {
        clients: pickedClients, sites: pickedSites, types: pickedTypes,
        search, condMin, condMax, sort, page,
      };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent("gbe:capital-filters-changed",
                                            { detail: payload }));
    } catch {}
  }, [pickedClients, pickedSites, pickedTypes, search, condMin, condMax, sort, page]);

  useEffect(() => {
    api.get("/capital/asset-register/filters")
       .then(({ data }) => setFacets(data))
       .catch(() => toast.error("Failed to load filter facets"));
  }, []);

  // Reflect the top-right client picker into this mounted register —
  // CapitalContext broadcasts `gbe:capital-filters-changed` on selection, but
  // without this listener the register only picked it up after a full reload.
  const pickedClientsRef = useRef(pickedClients);
  useEffect(() => { pickedClientsRef.current = pickedClients; }, [pickedClients]);
  useEffect(() => {
    const onSameWindow = (e) => {
      const next = e.detail?.clients || [];
      if (JSON.stringify(pickedClientsRef.current) !== JSON.stringify(next)) {
        setPickedClients(next);
        setPickedSites([]);
        setPage(1);
      }
    };
    window.addEventListener("gbe:capital-filters-changed", onSameWindow);
    return () => window.removeEventListener("gbe:capital-filters-changed", onSameWindow);
  }, []);

  const loadRows = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({
        page, page_size: pageSize, sort,
        clients: pickedClients.join(","),
        sites: pickedSites.join(","),
        asset_types: pickedTypes.join(","),
        search,
        condition_min: condMin,
        condition_max: condMax,
      });
      const { data } = await api.get(`/capital/asset-register?${params}`);
      setRows(data.rows || []);
      setSummary(data.summary || {});
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load asset register");
    } finally { setBusy(false); }
  };
  useEffect(() => { if (facets) loadRows(); /* eslint-disable-next-line */ },
    [page, sort, pickedClients, pickedSites, pickedTypes, condMin, condMax, facets]);

  // Debounced search — trigger reload 350ms after typing stops
  useEffect(() => {
    if (!facets) return;
    const id = setTimeout(() => { setPage(1); loadRows(); }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [search]);

  const toggle = (list, setList, val) => {
    setPage(1);
    setList((prev) => prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]);
  };
  const clearAll = () => {
    setPickedClients([]); setPickedSites([]); setPickedTypes([]);
    setSearch(""); setCondMin(0); setCondMax(5); setPage(1);
  };

  // Sites list is dynamic — if clients are picked, show only their sites
  const visibleSites = useMemo(() => {
    if (!facets) return [];
    if (pickedClients.length === 0) {
      // Flatten all sites
      return Object.entries(facets.sites_by_client).flatMap(([c, sites]) =>
        sites.map((s) => ({ ...s, client: c })));
    }
    return pickedClients.flatMap((c) =>
      (facets.sites_by_client[c] || []).map((s) => ({ ...s, client: c })));
  }, [facets, pickedClients]);

  // Prune picked sites that no longer belong to picked clients
  useEffect(() => {
    if (!facets || pickedClients.length === 0) return;
    const legal = new Set(visibleSites.map((s) => s.site));
    const filtered = pickedSites.filter((s) => legal.has(s));
    if (filtered.length !== pickedSites.length) setPickedSites(filtered);
    // eslint-disable-next-line
  }, [pickedClients, facets]);

  if (!facets) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const anyFilter = pickedClients.length || pickedSites.length || pickedTypes.length || search || condMin || condMax < 5;

  return (
    <div className="grid xl:grid-cols-[280px_minmax(0,1fr)] gap-4">
      {/* -------- Left rail: filters -------- */}
      <aside className="bg-white border border-slate-200 rounded-lg p-3 self-start space-y-3 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Filters</div>
          {anyFilter ? (
            <button type="button" onClick={clearAll}
              data-testid="asset-register-clear-filters"
              className="text-[10px] text-gbe hover:underline">Clear all</button>
          ) : null}
        </div>
        <FilterSection
          icon={Building2} label="Clients" count={facets.clients.length}
          expanded={expanded.clients}
          onToggle={() => setExpanded((e) => ({ ...e, clients: !e.clients }))}>
          <FilterSearch value={clientQ} onChange={setClientQ} placeholder="Search clients…" testid="asset-register-client-search" />
          <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1 mt-1">
            {facets.clients
              .filter((c) => !clientQ || c.client.toLowerCase().includes(clientQ.toLowerCase()))
              .map((c) => (
                <label key={c.client} className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                  <input type="checkbox" checked={pickedClients.includes(c.client)}
                    onChange={() => toggle(pickedClients, setPickedClients, c.client)}
                    data-testid={`asset-register-client-${c.client}`}
                    className="w-3 h-3" />
                  <span className="flex-1 truncate" title={c.client}>{c.client}</span>
                  <span className="text-slate-400 tabular-nums text-[10px]">{fmtN(c.count)}</span>
                </label>
              ))}
          </div>
        </FilterSection>

        <FilterSection
          icon={MapPin} label="Sites" count={visibleSites.length}
          expanded={expanded.sites}
          onToggle={() => setExpanded((e) => ({ ...e, sites: !e.sites }))}>
          <FilterSearch value={siteQ} onChange={setSiteQ} placeholder="Search sites…" testid="asset-register-site-search" />
          <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1 mt-1">
            {visibleSites.length === 0
              ? <div className="text-[10px] text-slate-400 italic px-1">Pick a client to filter sites</div>
              : visibleSites
                  .filter((s) => !siteQ || s.site.toLowerCase().includes(siteQ.toLowerCase()))
                  .map((s) => (
                    <label key={`${s.client}::${s.site}`} className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                      <input type="checkbox" checked={pickedSites.includes(s.site)}
                        onChange={() => toggle(pickedSites, setPickedSites, s.site)}
                        data-testid={`asset-register-site-${s.site}`}
                        className="w-3 h-3" />
                      <span className="flex-1 truncate" title={s.site}>{s.site}</span>
                      <span className="text-slate-400 tabular-nums text-[10px]">{fmtN(s.count)}</span>
                    </label>
                  ))}
          </div>
        </FilterSection>

        <FilterSection
          icon={Layers} label="Asset Types" count={facets.asset_types.length}
          expanded={expanded.types}
          onToggle={() => setExpanded((e) => ({ ...e, types: !e.types }))}>
          <FilterSearch value={typeQ} onChange={setTypeQ} placeholder="Search asset types…" testid="asset-register-type-search" />
          <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1 mt-1">
            {facets.asset_types
              .filter((t) => !typeQ || t.toLowerCase().includes(typeQ.toLowerCase()))
              .map((t) => (
                <label key={t} className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                  <input type="checkbox" checked={pickedTypes.includes(t)}
                    onChange={() => toggle(pickedTypes, setPickedTypes, t)}
                    data-testid={`asset-register-type-${t}`}
                    className="w-3 h-3" />
                  <span className="flex-1 truncate" title={t}>{t}</span>
                </label>
              ))}
          </div>
        </FilterSection>

        <div className="pt-2 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Condition (1-5)</div>
          <div className="flex items-center gap-2 text-[11px]">
            <label className="flex items-center gap-1">
              Min <input type="number" min="0" max="5" value={condMin}
                onChange={(e) => { setCondMin(+e.target.value); setPage(1); }}
                data-testid="asset-register-cond-min"
                className="w-12 h-6 text-[11px] px-1 border border-slate-200 rounded" />
            </label>
            <label className="flex items-center gap-1">
              Max <input type="number" min="1" max="5" value={condMax}
                onChange={(e) => { setCondMax(+e.target.value); setPage(1); }}
                data-testid="asset-register-cond-max"
                className="w-12 h-6 text-[11px] px-1 border border-slate-200 rounded" />
            </label>
          </div>
        </div>
      </aside>

      {/* -------- Right: KPIs + table -------- */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <SummaryTile label="Assets"        value={fmtN(summary.asset_count)} tone="default" testid="asset-register-kpi-count" />
          {summary.total_cost != null && (
            <SummaryTile label="Replacement $" value={fmt$(summary.total_cost)}  tone="info" testid="asset-register-kpi-cost" />
          )}
          <SummaryTile label="Avg Condition" value={summary.avg_condition ?? "—"} tone="default" testid="asset-register-kpi-avg-cond" />
          <SummaryTile label="High Risk (4-5)" value={fmtN(summary.high_risk)} tone={summary.high_risk > 0 ? "danger" : "default"} testid="asset-register-kpi-high-risk" />
          <SummaryTile label="Poor Condition (4-5)" value={fmtN(summary.poor_condition)} tone={summary.poor_condition > 0 ? "warning" : "default"} testid="asset-register-kpi-poor-cond" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search by asset name, ID or location…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="asset-register-search"
                className="h-8 text-xs pl-6" />
              {search && <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-3 h-3 text-slate-400 hover:text-slate-700" /></button>}
            </div>
            <div className="text-[11px] text-slate-500 ml-auto">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
              {fmtN(total)} match{total === 1 ? "" : "es"} · Page {page} / {Math.max(1, Math.ceil(total / pageSize))}
            </div>
            <Button size="sm" variant="outline" onClick={loadRows}
              data-testid="asset-register-refresh"
              className="h-7 text-[10px]"><RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} />Refresh</Button>
          </div>

          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <SortableTh label="Client" field="client_name" sort={sort} onSort={setSort} />
                  <SortableTh label="Site" field="location_name" sort={sort} onSort={setSort} />
                  <SortableTh label="Asset Name" field="asset_name" sort={sort} onSort={setSort} />
                  <SortableTh label="Type" field="asset_type" sort={sort} onSort={setSort} />
                  <SortableTh label="Location" field="cfas_asset_location" sort={sort} onSort={setSort} />
                  <SortableTh label="Cost" field="cost" sort={sort} onSort={setSort} align="right" />
                  <SortableTh label="Cond." field="cfas_condition" sort={sort} onSort={setSort} align="right" />
                  <SortableTh label="Risk" field="cfas_asset_risk" sort={sort} onSort={setSort} align="right" />
                  <SortableTh label="Install" field="cfas_asset_installation_date_estimate" sort={sort} onSort={setSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const detailId = r.asset_id || r.api_assetid || r.tag_id;
                  const detailPath = detailId ? `/asset/${encodeURIComponent(detailId)}` : null;
                  return (
                  <tr key={r._id}
                      className={`border-b border-slate-50 hover:bg-slate-50/60 ${detailPath ? "cursor-pointer" : ""}`}
                      onClick={() => detailPath && navigate(detailPath, { state: { from: "register" } })}
                      data-testid={`asset-register-row-${r.api_assetid}`}>
                    <td className="px-3 py-1.5 text-slate-700 truncate max-w-[160px]" title={r.client_name}>{r.client_name || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-700 truncate max-w-[160px]" title={r.location_name}>{r.location_name || "—"}</td>
                    <td className="px-3 py-1.5 font-medium text-slate-900 truncate max-w-[180px]" title={r.asset_name}>
                      {detailPath ? (
                        <Link to={detailPath} state={{ from: "register" }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`asset-register-link-${r.api_assetid}`}
                              className="text-[#003a5c] hover:underline">{r.asset_name || "—"}</Link>
                      ) : (r.asset_name || "—")}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">
                      {r.asset_type ? <Badge className="text-[9px] bg-slate-100 text-slate-700 border border-slate-200 font-normal">{r.asset_type}</Badge> : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500 truncate max-w-[160px]" title={r.cfas_asset_location}>{r.cfas_asset_location || "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.cost ? fmt$(r.cost) : "—"}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${r.cfas_condition >= 4 ? "text-rose-700 font-semibold" : r.cfas_condition >= 3 ? "text-amber-700" : ""}`}>{r.cfas_condition ?? "—"}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${r.cfas_asset_risk >= 4 ? "text-rose-700 font-semibold" : ""}`}>{r.cfas_asset_risk ?? "—"}</td>
                    <td className="px-3 py-1.5 text-slate-500">{(r.cfas_asset_installation_date_estimate || "").slice(0, 10) || "—"}</td>
                  </tr>
                  );
                })}
                {rows.length === 0 && !busy && (
                  <tr><td colSpan="9" className="px-3 py-10 text-center text-slate-500 text-xs">No assets match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-[11px]">
            <div className="text-slate-500">Showing {rows.length ? (page - 1) * pageSize + 1 : 0}-{(page - 1) * pageSize + rows.length} of {fmtN(total)}</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(1)}
                data-testid="asset-register-page-first" className="h-6 text-[10px]">First</Button>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                data-testid="asset-register-page-prev" className="h-6 text-[10px]">Prev</Button>
              <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}
                data-testid="asset-register-page-next" className="h-6 text-[10px]">Next</Button>
              <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage(Math.ceil(total / pageSize))}
                data-testid="asset-register-page-last" className="h-6 text-[10px]">Last</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function FilterSection({ icon: Icon, label, count, expanded, onToggle, children }) {
  return (
    <div className="border-b border-slate-100 pb-2 last:border-b-0">
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-1.5 text-[11px] font-medium text-slate-700 hover:text-slate-900 py-1">
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Icon className="w-3 h-3" />
        <span className="flex-1 text-left">{label}</span>
        <span className="text-[10px] text-slate-400 tabular-nums">{count}</span>
      </button>
      {expanded && <div className="mt-1">{children}</div>}
    </div>
  );
}


function FilterSearch({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative">
      <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full h-6 text-[10px] pl-6 pr-6 border border-slate-200 rounded" />
      {value && <button type="button" onClick={() => onChange("")}
        className="absolute right-1 top-1/2 -translate-y-1/2"><X className="w-3 h-3 text-slate-400 hover:text-slate-700" /></button>}
    </div>
  );
}


function SummaryTile({ label, value, tone, testid }) {
  const cls = tone === "danger"  ? "border-rose-200 bg-rose-50/40 text-rose-700"
           : tone === "warning" ? "border-amber-200 bg-amber-50/40 text-amber-700"
           : tone === "info"    ? "border-sky-200 bg-sky-50/40 text-sky-700"
           : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-lg border ${cls} px-3 py-2`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-display font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}


function SortableTh({ label, field, sort, onSort, align = "left" }) {
  const [f, dir] = (sort || "").split(":");
  const active = f === field;
  const nextDir = active && dir === "desc" ? "asc" : "desc";
  return (
    <th className={`text-${align} font-medium px-3 py-1.5 select-none cursor-pointer hover:text-slate-800 ${active ? "text-slate-800" : ""}`}
        onClick={() => onSort(`${field}:${nextDir}`)}
        data-testid={`asset-register-sort-${field}`}>
      {label} {active && (dir === "desc" ? "↓" : "↑")}
    </th>
  );
}
